import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { UserProvider, useUser } from '@/contexts/UserContext';
import OfflineIndicator from '@/components/OfflineIndicator';
import SyncStatus from '@/components/SyncStatus';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));

function LoadingSpinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center" data-testid="loading">
      <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useUser();

  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

/**
 * NavigationGuard component - handles bfcache and visibility changes.
 * Addresses issue #615 - Browser back navigation state loss.
 */
function NavigationGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.dispatchEvent(new Event('bfcache-restore'));
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        window.dispatchEvent(new Event('page-visible'));
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    document.body.classList.remove('navigating');
  }, [location]);

  return <>{children}</>;
}

/**
 * Root application component with routing, auth, and global UI elements.
 *
 * PWA Offline Support:
 * - OfflineIndicator: Shows offline mode badge + reconnection toast
 * - SyncStatus: Shows pending sync item count
 *
 * Z-index hierarchy:
 * - z-40: Offline/Sync indicators (persistent UI)
 * - z-50: Toast notifications (OfflineIndicator reconnection toast)
 * - z-100+: Modals and dialogs (highest priority)
 */
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UserProvider>
          <Router>
            <NavigationGuard>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/admin/feedback"
                    element={
                      <ProtectedRoute>
                        <AdminFeedback />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>

              <div
                className="fixed top-4 right-4 z-40"
                data-testid="offline-indicator-container"
                aria-label="Network status indicator"
              >
                <OfflineIndicator />
              </div>

              <div
                className="fixed bottom-4 right-4 z-40"
                data-testid="sync-status-container"
                aria-label="Sync queue status"
              >
                <SyncStatus />
              </div>

              <Toaster />
            </NavigationGuard>
          </Router>
        </UserProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
